import round from 'lodash/round';
import type { DocumentNode } from 'graphql';
import type { Person } from '@atlassian/jira-filters';
import type { IssueId, IssueKey, IssueTypeId, ProjectId } from '@atlassian/jira-shared-types';

/**
 * Base
 */

// @ts-expect-error - TS2456 - Type alias 'Partial' circularly references itself. | TS2315 - Type 'Partial' is not generic.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Partial<T> = Partial<Flow.Diff<T, Record<any, any>>>;

export type Seconds = number;
export type Hours = number;
export type Url = string;
export type HtmlString = string;
export type Color = string;

export const secondsToHours = (seconds: Seconds, roundDigit = 0): Hours =>
	round(seconds / 60 / 60, roundDigit);

export const secondsToDays = (seconds: number): number => secondsToHours(seconds) / 24;

export type DateRange = {
	startDateInclusive: Date;
	endDateExclusive: Date;
};

/**
 * Controller
 */

export type EpicIdOption = IssueId | null;

// Note: an empty string/array filter value means "do not filter"
//       null in epicIds means give me issues without epic
export type TopFilter = {
	epicIds: EpicIdOption[];
	issueTypeIds: IssueTypeId[];
};

/**
 * Chart
 */

export type ChartCustomInfoLoading = {
	loading: true;
};

export type ChartCustomInfoLoaded = {
	loading: false;
	issueKey: IssueKey;
	reviewTime: Hours | null;
	tooltipFormatCycleTime: Seconds;
	numPullRequests: number;
	numCommits: number;
};

export type ChartCustomInfoError = {
	loading: false;
	error: Error;
};

export type ChartCustomInfo = ChartCustomInfoLoading | ChartCustomInfoLoaded | ChartCustomInfoError;

export type ChartDataPoint = {
	value: Seconds;
	deployedDate: Date;
	customInfo: ChartCustomInfo;
};

export type ChartData = {
	hasPermissionForAllContributingIssues: boolean;
	periodCycleTimeInSeconds: number;
	hasEnoughDataForMeaningfulStatistics: boolean;
	wholePeriodCycleTimeInSeconds: number;
	dataPoints: ChartDataPoint[];
};

export type TooltipFormatter<T> = (arg1: T) => HtmlString | null;

/**
 * Historical Bar Chart
 */

export type BarDataCustomInfo = DateRange & {
	numIssues: number;
	emphasisColor: string | null;
	tooltipFormatCycleTime: Seconds;
};
export type BarChartDataPoint = {
	value: number;
	itemStyle: {
		color: string;
	};
	emphasis: {
		itemStyle: {
			color: string;
		};
	};
	customInfo: BarDataCustomInfo;
};

export type BarChartTooltipFormatterParams = {
	data: BarChartDataPoint;
};

// These properties are only a portion of the original object, which we use in our components since
// ECharts doesn't support Flow
export type EChartsEvent = {
	componentType: string;
	seriesType: string;
	dataIndex: number;
	data: BarChartDataPoint;
	value: number;
	name: string;
};

export type Trend = 'under' | 'over' | 'equal';

/**
 * Phase overview
 */

export type PhaseSummary = {
	duration: Hours;
};

export type PeriodSummaries = {
	development: PhaseSummary;
	review: PhaseSummary;
	deployment: PhaseSummary;
};

/**
 * Issue Shipped Table
 */

export type IssueTableIssueCol = {
	id: IssueId;
	key: IssueKey;
	typeIconUrl: Url;
	summary: string;
	link: Url;
};

export type IssueTableRow = {
	cycleTime: Seconds;
	issue: IssueTableIssueCol;
	numCommits: number;
	numPullRequests: number;
	reviewTime: Seconds | null;
	dateDeployed: Date;
};

export type IssueTableData = {
	rows: IssueTableRow[];
	cycleTimeStatistic: number | null;
};

export type Epic = {
	id: IssueId;
	summary: string;
};

export type ItemType = {
	id: IssueTypeId;
	name: string;
	iconUrl: string;
};

/**
 * Issue
 */
export type IssueType = {
	id: IssueTypeId;
	name: string;
	iconUrl: string;
};

export type Issue = {
	id: IssueId;
	key: IssueKey;
	typeId: IssueTypeId;
	projectId: ProjectId;
	summary: string;
	assignee: Person | null;
};

export type MockRequest<RequestVariables> = {
	query: DocumentNode;
	variables: RequestVariables;
};

export type MockResult<ResultData> = {
	data?: ResultData;
	errors?: Error[];
};

export type MockResponse<ResultData, RequestVariables> = {
	request: MockRequest<RequestVariables>;
	result: MockResult<ResultData>;
	delay?: number;
};

/**
 * IssueMetrics
 */

export type IssueMetrics = {
	id: IssueId;
	totalCycleTimeSeconds: Seconds;
	meanReviewTimeSeconds: Seconds | null;
	commitsCount: number;
	pullRequestsCount: number;
	lastSuccessfulProductionDeployment: Date;
};
